import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import { useConfig } from '@/composables/config'
import {
    INDEX_EXCHANGE,
    MEDIA_NET_BANNER,
    MEDIA_NET_MULTIFORMAT,
    MEDIA_NET_VIDEO,
    NEXVERSE,
    ONETAG,
    PLAYGAMA_FALLBACK,
    PLAYGAMA,
    TAPPX_BANNER,
    TAPPX_VIDEO,
    STARTIO,
    EQUATIV,
    INMOBI_BANNER,
    INMOBI_VIDEO,
} from '@/modules/adv/header-bidding/bidders-config'
import { AdType } from '@/modules/adv/types'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'
import { useNativeAppStore } from '@/store/native-app-store'
import {
    AdProviderTypes,
    HeaderBiddingAdSettings,
    type AdSenseProviderSettings,
    type AppProviderSettings,
    type EffectiveType,
    type GameDistributionsSettings,
    type GoogleAdBannerSettings,
    type GoogleAdSettings,
    type GoogleInstreamSettings,
} from '@/types'

// Factory Types - require adUnitConfig, make rest optional

type GoogleAdOverrides = Pick<GoogleAdSettings['options'], 'adUnitConfig'> &
    Partial<Omit<GoogleAdSettings['options'], 'adUnitConfig'>>

type GoogleBannerOverrides = Pick<GoogleAdBannerSettings['options'], 'adUnitConfig'> &
    Partial<Omit<GoogleAdBannerSettings['options'], 'adUnitConfig'>>

type GoogleInstreamOverrides = Pick<GoogleInstreamSettings['options'], 'adUnitConfig'> &
    Partial<Omit<GoogleInstreamSettings['options'], 'adUnitConfig' | 'name'>>

type AppNativeOverrides = Pick<AppProviderSettings['options'], 'adUnitConfig'> &
    Partial<Omit<AppProviderSettings['options'], 'adUnitConfig'>>

type AdSenseOverrides = Pick<AdSenseProviderSettings['options'], 'adUnitConfig'> &
    Partial<Omit<AdSenseProviderSettings['options'], 'adUnitConfig'>>

// Constants

const DEFAULT_AD_UNIT_TIMEOUT = 3000
const SLOW_CONNECTION_TIMEOUT = 6000
const APP_NATIVE_TIMEOUT = 6000
const INSTREAM_TIMEOUT = 5000

const GOOGLE_AD_PAGE_DOMAIN = 'playgama.com'
const GOOGLE_AD_PAGE_URL = 'https://playgama.com'

const VERY_SLOW_EFFECTIVE_TYPES = ['2g', 'slow-2g']
const SLOW_EFFECTIVE_TYPES = [...VERY_SLOW_EFFECTIVE_TYPES, '3g']

const STATIC_GOOGLE_AD_UNITS: Record<string, string> = {
    sticky_mobile_non_game_page: 'sticky_mobile',
    sticky_mobile: 'sticky_mobile',
    sticky_tablet: 'sticky_mobile',
    sticky_desktop: 'sticky_desktop',
    sidebar: 'sidebar',
    sidebar_bottom: 'sidebar_bottom',
    sidebar_fullscreen: 'sidebar_fullscreen',
    sidebar_bottom_fullscreen: 'sidebar_bottom_fullscreen',
    leaderboard: 'leaderboard',
    leaderboard_rest: 'leaderboard_rest',
    widget_sidebar: 'widget_sidebar',
    widget_sidebar_bottom: 'widget_sidebar_bottom',
    widget_horizontal: 'widget_horizontal',
    widget_horizontal_2: 'widget_horizontal_2',
    widget_sticky_mobile: 'widget_sticky_mobile',
    widget_sticky_tablet: 'widget_sticky_mobile',
    interstitial_on_return: 'interstitial_on_return',
    universal_banner_1: 'universal_banner_1',
    universal_banner_short_1: 'universal_banner_1',
    universal_banner_2: 'universal_banner_2',
}

const AD_SENSE_PORTAL_CLIENT_ID = 'ca-pub-2864899775153797'
const STATIC_GOOGLE_AD_SENSE_UNITS: Record<string, string> = {
    sticky_mobile: '2256255959',
    sticky_desktop: '2256255959',
}

// Native (AppLovin) ad units — used once the native SDK ships (Android ≥ 1.1.09 / iOS ≥ 1.1.3).
const ANDROID_APPLOVIN_AD_UNITS: Partial<Record<AdType, string>> = {
    interstitial: 'adc0a523dde2c2f1',
    rewarded: 'd0ef11af6f54f0fb',
}
const IOS_APPLOVIN_AD_UNITS: Partial<Record<AdType, string>> = {
    interstitial: 'da96af9802e81f8f',
    rewarded: '23193cc48219498c',
}

// GAM fallback ad units for older app versions without the native SDK.
const ANDROID_GAM_AD_UNITS: Partial<Record<AdType, string>> = {
    interstitial: '/23211928466/interstitial_android',
    interstitial_preroll: '/23211928466/interstitial_preroll_android',
    rewarded: '/23211928466/rewarded_android',
}
const IOS_GAM_AD_UNITS: Partial<Record<AdType, string>> = {
    interstitial: '/23211928466/interstitial_ios',
    interstitial_preroll: '/23211928466/interstitial_preroll_ios',
    rewarded: '/23211928466/rewarded_ios',
}
const TEST_GAM_AD_UNITS: Partial<Record<AdType, string>> = {
    interstitial: '/21775744923/example/interstitial',
    interstitial_preroll: '/21775744923/example/interstitial',
    rewarded: '/21775744923/example/rewarded',
}

// Composable

export function useAdProvidersSettings() {
    const { VUE_APP_AD_MANAGER_ACCOUNT } = useConfig()
    const router = useRouter()
    const { sdk: appNativeSDK } = storeToRefs(useNativeAppStore())
    const appStore = useAppStore()
    const { platform, effectiveType, countryCode } = storeToRefs(appStore)

    // Computed Values
    const isDesktop = computed(() => platform.value === 'desktop')
    const hasAndroidApplovinSdk = computed(() => appStore.isVersionAtLeast({ android: '1.1.09' }))
    const hasIosApplovinSdk = computed(() => appStore.isVersionAtLeast({ ios: '1.1.3' }))

    const androidNativeAdUnits = computed(() =>
        hasAndroidApplovinSdk.value ? ANDROID_APPLOVIN_AD_UNITS : ANDROID_GAM_AD_UNITS,
    )
    const iosNativeAdUnits = computed(() => (hasIosApplovinSdk.value ? IOS_APPLOVIN_AD_UNITS : IOS_GAM_AD_UNITS))
    const androidTestNativeAdUnits = computed(() =>
        hasAndroidApplovinSdk.value ? ANDROID_APPLOVIN_AD_UNITS : TEST_GAM_AD_UNITS,
    )
    const iosTestNativeAdUnits = computed(() => (hasIosApplovinSdk.value ? IOS_APPLOVIN_AD_UNITS : TEST_GAM_AD_UNITS))
    const isSlowConnection = computed(() => SLOW_EFFECTIVE_TYPES.includes(effectiveType.value as EffectiveType))
    const isVerySlowConnection = computed(() =>
        VERY_SLOW_EFFECTIVE_TYPES.includes(effectiveType.value as EffectiveType),
    )
    const providerTimeout = computed(() =>
        isVerySlowConnection.value ? SLOW_CONNECTION_TIMEOUT : DEFAULT_AD_UNIT_TIMEOUT,
    )

    // Factory: Google Ad

    const createGoogleAdSettings = (overrides: GoogleAdOverrides): GoogleAdSettings => ({
        type: AdProviderTypes.GOOGLE_AD,
        options: {
            platform: platform.value,
            pageDomain: GOOGLE_AD_PAGE_DOMAIN,
            gamAccount: VUE_APP_AD_MANAGER_ACCOUNT,
            timeout: DEFAULT_AD_UNIT_TIMEOUT,
            ...overrides,
        },
    })

    // Factory: Google Banner

    const createGoogleBannerSettings = (overrides: GoogleBannerOverrides): GoogleAdBannerSettings => ({
        type: AdProviderTypes.GOOGLE_BANNER,
        options: {
            platform: platform.value,
            pageDomain: GOOGLE_AD_PAGE_DOMAIN,
            gamAccount: VUE_APP_AD_MANAGER_ACCOUNT,
            timeout: DEFAULT_AD_UNIT_TIMEOUT,
            ...overrides,
        },
    })

    // Factory: Google Instream

    const portalBuildAdTagParams = (): Record<string, string | number> => {
        const gamePageUrl = `${GOOGLE_AD_PAGE_URL}${
            router.resolve({
                name: ROUTE_NAMES.GAME_PAGE,
                params: { game: router.currentRoute.value.params.game },
            }).fullPath
        }`

        return {
            url: gamePageUrl,
            description_url: gamePageUrl,
        }
    }

    const createGoogleInstreamSettings = (overrides: GoogleInstreamOverrides): GoogleInstreamSettings => ({
        type: AdProviderTypes.GOOGLE_INSTREAM,
        options: {
            headerBidding: {
                bidders: [PLAYGAMA],
            },
            platform: platform.value,
            pageURL: GOOGLE_AD_PAGE_URL,
            gamAccount: VUE_APP_AD_MANAGER_ACCOUNT,
            timeout: INSTREAM_TIMEOUT,
            buildAdditionalAdParams: portalBuildAdTagParams,
            ...overrides,
        },
    })

    // Factory: App Native

    const createAppNativeSettings = (overrides: AppNativeOverrides): AppProviderSettings => ({
        type: AdProviderTypes.APP,
        options: {
            appNativeSDK: appNativeSDK.value,
            timeout: APP_NATIVE_TIMEOUT,
            platform: platform.value,
            ...overrides,
        },
    })

    // Factory: Header-bidding-ad

    const createHeaderBiddingAdSettings = (
        bidders: HeaderBiddingAdSettings['options']['bidders'],
    ): HeaderBiddingAdSettings => ({
        type: AdProviderTypes.HEADER_BIDDING_AD,
        options: {
            targeting: {},
            timeout: DEFAULT_AD_UNIT_TIMEOUT,
            platform: platform.value,
            bidders,
        },
    })

    // Factory: Ad-sense
    const createAdSenseSettings = (overrides: AdSenseOverrides): AdSenseProviderSettings => ({
        type: AdProviderTypes.GOOGLE_AD_SENSE,
        options: {
            platform: platform.value,
            clientId: AD_SENSE_PORTAL_CLIENT_ID,
            timeout: providerTimeout.value,
            ...overrides,
        },
    })

    // Google Ad Providers

    const GOOGLE_AD_PORTAL_SETTINGS = createGoogleAdSettings({
        adUnitConfig: {
            desktop: STATIC_GOOGLE_AD_UNITS,
            mobile: STATIC_GOOGLE_AD_UNITS,
        },
        headerBidding: {
            bidders: [ONETAG, TAPPX_BANNER, MEDIA_NET_BANNER, PLAYGAMA, INMOBI_BANNER, EQUATIV, INDEX_EXCHANGE],
        },
    })

    const GOOGLE_AD_GAME_PAGE_SETTINGS = createGoogleAdSettings({
        timeout: providerTimeout.value,
        adUnitConfig: {
            desktop: {
                ...STATIC_GOOGLE_AD_UNITS,
                rewarded: 'rewarded_desktop',
                interstitial: 'h5_interstitial_desktop',
                interstitial_preroll: 'h5_interstitial_preroll_desktop',
            },
            mobile: {
                ...STATIC_GOOGLE_AD_UNITS,
                rewarded: 'rewarded_mobile',
                interstitial: 'h5_interstitial_mobile',
                interstitial_preroll: 'h5_interstitial_preroll_mobile',
            },
        },
        ...(isDesktop.value && {
            headerBidding: {
                bidders: [PLAYGAMA],
            },
        }),
    })

    const GOOGLE_AD_WIDGET_SETTINGS = createGoogleAdSettings({
        adUnitConfig: {
            desktop: {
                rewarded: 'widget_rewarded_desktop',
                interstitial: 'widget_h5_interstitial_desktop',
                interstitial_preroll: 'widget_h5_interstitial_preroll_desktop',
            },
            mobile: {
                rewarded: 'widget_rewarded_mobile',
                interstitial: 'widget_h5_interstitial_mobile',
                interstitial_preroll: 'widget_h5_interstitial_preroll_mobile',
            },
        },
    })

    // Google Banner Providers

    const GOOGLE_BANNER_GAME_PAGE_SETTINGS = createGoogleBannerSettings({
        timeout: providerTimeout.value,
        headerBidding: {
            bidders: [
                countryCode.value === 'IN' && NEXVERSE,
                MEDIA_NET_BANNER,
                PLAYGAMA,
                INMOBI_BANNER,
                EQUATIV,
                INDEX_EXCHANGE,
                ONETAG,
                TAPPX_BANNER,
            ].filter((x) => !!x),
        },
        adUnitConfig: {
            desktop: {
                interstitial_preroll: 'banner_ingame_preroll_desktop',
                interstitial: 'banner_ingame_desktop',
                rewarded: 'banner_ingame_rewarded_desktop',
            },
            mobile: {
                interstitial_preroll: 'banner_ingame_preroll_mobile',
                interstitial: 'banner_ingame_mobile',
                rewarded: 'banner_ingame_rewarded_mobile',
            },
        },
    })

    // Google Instream Providers

    const GOOGLE_INSTREAM_GAME_PAGE_SETTINGS = createGoogleInstreamSettings({
        adUnitConfig: {
            desktop: {
                rewarded: 'instream_rewarded_desktop',
                interstitial: 'instream_desktop',
                interstitial_preroll: 'instream_preroll_desktop',
            },
            mobile: {
                rewarded: 'instream_rewarded_mobile',
                interstitial: 'instream_mobile',
                interstitial_preroll: 'instream_preroll_mobile',
            },
        },
        headerBidding: {
            bidders: [MEDIA_NET_VIDEO, PLAYGAMA, INMOBI_VIDEO, EQUATIV, INDEX_EXCHANGE, TAPPX_VIDEO, ONETAG],
        },
    })

    const GOOGLE_INSTREAM_CHEAP_MINIPLAY_SETTINGS = createGoogleInstreamSettings({
        adUnitConfig: {
            desktop: {
                interstitial_preroll: 'instream_preroll_no_floor_desktop',
                interstitial: 'instream_no_floor_desktop',
                rewarded: 'instream_rewarded_no_floor_desktop',
            },
            mobile: {
                interstitial_preroll: 'instream_preroll_no_floor_mobile',
                interstitial: 'instream_no_floor_mobile',
                rewarded: 'instream_rewarded_no_floor_mobile',
            },
        },
    })

    const GOOGLE_INSTREAM_WIDGET_SETTINGS = createGoogleInstreamSettings({
        timeout: DEFAULT_AD_UNIT_TIMEOUT,
        adUnitConfig: {
            desktop: {
                rewarded: 'widget_instream_rewarded_desktop',
                interstitial: 'widget_instream_desktop',
                interstitial_preroll: 'widget_instream_preroll_desktop',
            },
            mobile: {
                rewarded: 'widget_instream_rewarded_mobile',
                interstitial: 'widget_instream_mobile',
                interstitial_preroll: 'widget_instream_preroll_mobile',
            },
        },
    })

    // App Native Providers

    const APP_NATIVE_ANDROID_SETTINGS = createAppNativeSettings({
        adUnitConfig: {
            mobile: androidNativeAdUnits.value,
            desktop: {},
        },
    })

    const APP_NATIVE_ANDROID_TEST_SETTINGS = createAppNativeSettings({
        adUnitConfig: {
            mobile: androidTestNativeAdUnits.value,
            desktop: {},
        },
    })

    const APP_NATIVE_IOS_SETTINGS = createAppNativeSettings({
        adUnitConfig: {
            mobile: iosNativeAdUnits.value,
            desktop: {},
        },
    })

    const APP_NATIVE_IOS_TEST_SETTINGS = createAppNativeSettings({
        adUnitConfig: {
            mobile: iosTestNativeAdUnits.value,
            desktop: {},
        },
    })

    // AdSense Provider — add adUnitConfig slot IDs + exp (channel) from OPPO Google Sheet after domain verification

    const GOOGLE_AD_SENSE_SETTINGS: AdSenseProviderSettings = createAdSenseSettings({
        adUnitConfig: {
            desktop: STATIC_GOOGLE_AD_SENSE_UNITS,
            mobile: STATIC_GOOGLE_AD_SENSE_UNITS,
        },
        targeting: {
            exp: '5883774659', // = channel group_control
        },
    })

    const HEADER_BIDDING_AD_FALLBACK_PROVIDER_SETTINGS = createHeaderBiddingAdSettings([
        TAPPX_VIDEO,
        MEDIA_NET_MULTIFORMAT,
        ONETAG,
        STARTIO,
        INMOBI_VIDEO,
        EQUATIV,
        INDEX_EXCHANGE,
        PLAYGAMA_FALLBACK,
    ])

    const HEADER_BIDDING_AD_BANNER_FALLBACK_PROVIDER_SETTINGS = createHeaderBiddingAdSettings([
        TAPPX_BANNER,
        MEDIA_NET_BANNER,
        ONETAG,
        STARTIO,
        INMOBI_BANNER,
        EQUATIV,
        INDEX_EXCHANGE,
        PLAYGAMA_FALLBACK,
    ])

    const HEADER_BIDDING_AD_NATIVE_APP_FALLBACK_PROVIDER_SETTINGS = createHeaderBiddingAdSettings([PLAYGAMA_FALLBACK])

    // Other Providers

    const GAME_DISTRIBUTION_PROVIDER_SETTINGS: GameDistributionsSettings = {
        type: AdProviderTypes.GAME_DISTRIBUTION,
        options: { timeout: DEFAULT_AD_UNIT_TIMEOUT },
    }

    // Provider Combinations

    const AD_PROVIDER_SETTINGS_ON_PORTAL = [
        GOOGLE_AD_PORTAL_SETTINGS,
        HEADER_BIDDING_AD_BANNER_FALLBACK_PROVIDER_SETTINGS,
    ]

    const AD_PROVIDER_SETTINGS_ON_PORTAL_NATIVE = [GOOGLE_AD_PORTAL_SETTINGS]

    const AD_PROVIDER_SETTINGS_FOR_EXPORT_GAME_PAGE = isSlowConnection.value
        ? [GOOGLE_AD_GAME_PAGE_SETTINGS, GOOGLE_BANNER_GAME_PAGE_SETTINGS]
        : [GOOGLE_AD_GAME_PAGE_SETTINGS, GOOGLE_INSTREAM_GAME_PAGE_SETTINGS, GOOGLE_BANNER_GAME_PAGE_SETTINGS]

    const AD_PROVIDER_SETTINGS_FOR_EXPORT_GAME_PAGE_ON_WIDGET = [
        GOOGLE_AD_WIDGET_SETTINGS,
        GOOGLE_INSTREAM_WIDGET_SETTINGS,
        GOOGLE_BANNER_GAME_PAGE_SETTINGS,
    ]

    const APP_NATIVE_ANDROID_PROVIDERS_SETTINGS = [APP_NATIVE_ANDROID_SETTINGS]
    const APP_NATIVE_ANDROID_TEST_PROVIDERS_SETTINGS = [APP_NATIVE_ANDROID_TEST_SETTINGS]
    const APP_NATIVE_IOS_PROVIDERS_SETTINGS = [APP_NATIVE_IOS_SETTINGS]
    const APP_NATIVE_IOS_TEST_PROVIDERS_SETTINGS = [APP_NATIVE_IOS_TEST_SETTINGS]

    const FALLBACK_AD_PROVIDER_SETTINGS_ON_PORTAL = [HEADER_BIDDING_AD_FALLBACK_PROVIDER_SETTINGS]

    const FALLBACK_AD_PROVIDER_SETTINGS_ON_PORTAL_NATIVE = [HEADER_BIDDING_AD_NATIVE_APP_FALLBACK_PROVIDER_SETTINGS]

    const FALLBACK_AD_PROVIDER_SETTINGS_PARTNER = [createHeaderBiddingAdSettings([PLAYGAMA_FALLBACK])]

    const MINIPLAY_PROVIDERS_SETTINGS = [GOOGLE_INSTREAM_GAME_PAGE_SETTINGS, GOOGLE_INSTREAM_CHEAP_MINIPLAY_SETTINGS]

    // Return

    return {
        // Portal
        AD_PROVIDER_SETTINGS_ON_PORTAL,
        AD_PROVIDER_SETTINGS_FOR_EXPORT_GAME_PAGE,
        AD_PROVIDER_SETTINGS_FOR_EXPORT_GAME_PAGE_ON_WIDGET,

        // Portal Native
        AD_PROVIDER_SETTINGS_ON_PORTAL_NATIVE,

        // Single providers
        GAME_DISTRIBUTION_PROVIDER_SETTINGS,

        // App native
        APP_NATIVE_ANDROID_PROVIDERS_SETTINGS,
        APP_NATIVE_ANDROID_TEST_PROVIDERS_SETTINGS,
        APP_NATIVE_IOS_PROVIDERS_SETTINGS,
        APP_NATIVE_IOS_TEST_PROVIDERS_SETTINGS,

        // Portal fallback
        FALLBACK_AD_PROVIDER_SETTINGS_ON_PORTAL,
        // Portal fallback native
        FALLBACK_AD_PROVIDER_SETTINGS_ON_PORTAL_NATIVE,

        // Partner fallback
        FALLBACK_AD_PROVIDER_SETTINGS_PARTNER,

        // Partner-specific
        MINIPLAY_PROVIDERS_SETTINGS,

        // AdSense
        GOOGLE_AD_SENSE_SETTINGS,
    }
}
